import React from 'react';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { FileText } from 'lucide-react';

const ProfileBioSection = ({ formData, errors, onChange }) => {
  const hasLinkedIn = formData?.hasLinkedIn || false;

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      // Validate file type (PDF only)
      if (file?.type !== 'application/pdf') {
        onChange('cvFile', null);
        onChange('cvFileName', '');
        // You could set an error here if needed
        return;
      }
      // Validate file size (max 5MB)
      if (file?.size > 5 * 1024 * 1024) {
        onChange('cvFile', null);
        onChange('cvFileName', '');
        return;
      }
      onChange('cvFile', file);
      onChange('cvFileName', file?.name);
    }
  };

  const handleRemoveFile = () => {
    onChange('cvFile', null);
    onChange('cvFileName', '');
    // Reset file input
    const fileInput = document.getElementById('cv-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <div className="space-y-6 pt-6 border-t border-[#E5E7EB]">
      <div>
        <h4 className="text-lg font-semibold text-[#111827] mb-2">
          Profile & Bio
        </h4>
        <p className="text-sm text-[#6B7280]">
          Help us understand your professional background and career goals
        </p>
      </div>

      {/* Bio Textarea */}
      <div className="space-y-2">
        <label
          htmlFor="bio"
          className="text-sm font-medium text-foreground"
        >
          Professional Bio
          <span className="text-destructive ml-1">*</span>
        </label>
        <textarea
          id="bio"
          placeholder="Describe your professional background, key achievements, and what you're looking for in your next opportunity..."
          value={formData?.bio || ''}
          onChange={(e) => onChange('bio', e?.target?.value)}
          rows={6}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
        />
        {errors?.bio && (
          <p className="text-sm text-destructive">{errors?.bio}</p>
        )}
        <p className="text-xs text-muted-foreground">
          Minimum 100 characters. This will be visible to employers anonymously.
        </p>
      </div>

      {/* LinkedIn Toggle */}
      <div className="space-y-4">
        <Checkbox
          label="I have a LinkedIn profile"
          description="We'll use your LinkedIn to verify your experience"
          checked={hasLinkedIn}
          onChange={(e) => {
            onChange('hasLinkedIn', e?.target?.checked);
            // Clear opposite field when toggling
            if (e?.target?.checked) {
              onChange('cvFile', null);
              onChange('cvFileName', '');
            } else {
              onChange('linkedinUrl', '');
            }
          }}
        />

        {/* LinkedIn URL Input - Show if has LinkedIn */}
        {hasLinkedIn && (
          <div className="ml-6 pl-4 border-l-2 border-[#0025cc]/20">
            <Input
              label="LinkedIn Profile URL"
              type="url"
              placeholder="https://linkedin.com/in/yourprofile"
              value={formData?.linkedinUrl || ''}
              onChange={(e) => onChange('linkedinUrl', e?.target?.value)}
              error={errors?.linkedinUrl}
              required
            />
          </div>
        )}

        {/* CV Upload - Show if no LinkedIn */}
        {!hasLinkedIn && (
          <div className="ml-6 pl-4 border-l-2 border-[#0025cc]/20 space-y-3">
            <div>
              <label className="text-sm font-medium text-foreground">
                Upload CV/Resume
                <span className="text-destructive ml-1">*</span>
              </label>
              <p className="text-xs text-muted-foreground mt-1">
                PDF format only, maximum 5MB
              </p>
            </div>

            {!formData?.cvFileName ? (
              <div className="relative">
                <input
                  type="file"
                  id="cv-upload"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="sr-only"
                />
                <label
                  htmlFor="cv-upload"
                  className="flex items-center justify-center gap-2 w-full h-24 border-2 border-dashed border-input rounded-lg hover:border-[#0025cc] hover:bg-[#0025cc]/5 transition-colors cursor-pointer"
                >
                  <FileText className="w-6 h-6 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Click to upload your CV
                  </span>
                </label>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-[#0025cc]/5 border border-[#0025cc]/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#0025cc]" />
                  <span className="text-sm font-medium text-[#111827]">
                    {formData?.cvFileName}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="text-sm text-destructive hover:text-destructive/80 font-medium"
                >
                  Remove
                </button>
              </div>
            )}

            {errors?.cvFile && (
              <p className="text-sm text-destructive">{errors?.cvFile}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileBioSection;